import { z } from 'zod'

import {
  AutoAssignmentActionType,
  AutoAssignmentConditionField,
  AutoAssignmentConditionOperator,
  AutoAssignmentConditionType,
  AutoAssignmentFilterOperator,
  AutoAssignmentRuleStatus,
  AutoAssignmentTriggerType,
} from 'common/settings/constants/AutoAssignmentTypes'

export const AutoAssignmentConditionSchema = z.object({
  value: z.string().or(z.number()),
  type: z.enum(AutoAssignmentConditionType),
  field: z.enum(AutoAssignmentConditionField),
  operator: z.enum(AutoAssignmentConditionOperator),
})

const AutoAssignmentFilterGroupSchema = z.object({
  items: z.array(AutoAssignmentConditionSchema),
  operator: z.enum(AutoAssignmentFilterOperator),
})

const AutoAssignmentFilterSchema = z.object({
  groups: z.array(AutoAssignmentFilterGroupSchema),
  operator: z.enum(AutoAssignmentFilterOperator),
})

const AutoAssignmentActionSchema = z.object({
  action_type: z.enum(AutoAssignmentActionType),
  action_target_id: z.number().optional(),
})

const AutoAssignmentRuleSchema = z.object({
  rule_id: z.number(),
  title: z.string(),
  filter: AutoAssignmentFilterSchema.or(z.null()).optional(),
  actions: z.array(AutoAssignmentActionSchema).or(z.null()).optional(),
  trigger_type: z.enum(AutoAssignmentTriggerType).or(z.null()),
  sort: z.number(),
  status: z.enum(AutoAssignmentRuleStatus),
  ts_created: z.number(),
  ts_updated: z.number(),
})

const AutoAssignmentRulesList = z.array(AutoAssignmentRuleSchema).optional()

const AutoAssignmentListSchema = z.object({
  list: AutoAssignmentRulesList,
})

const CreateAutoAssignmentRulePayloadSchema = AutoAssignmentRuleSchema.partial().extend({
  title: z.string(),
})

const AutoAssignmentRuleResponseSchema = z.object({
  rule: AutoAssignmentRuleSchema.optional(),
})

const UpdateAutoAssignmentRulePayloadSchema = z.object({
  rule_id: z.number(),
  trigger_type: z.enum(AutoAssignmentTriggerType),
  filter: AutoAssignmentFilterSchema.or(z.null()).optional(),
  actions: z.array(AutoAssignmentActionSchema).or(z.null()).optional(),
})

const SetAutoAssignmentRuleStatusPayloadSchema = z.object({
  rule_id: z.number(),
  status: z.enum(AutoAssignmentRuleStatus),
})

const SetAutoAssignmentRuleTitlePayloadSchema = z.object({
  rule_id: z.number(),
  title: z.string(),
})

const DeleteAutoAssignmentRulePayloadSchema = z.object({
  rule_id: z.number(),
})

const DuplicateAutoAssignmentRulePayloadSchema = z.object({
  rule_id: z.number(),
})

const SortAutoAssignmentRulesPayloadSchema = z.object({
  rule_ids: z.array(z.number()),
})

export const AutoAssignmentApiSchemas = {
  getAutoAssignmentRulesList: {
    request: z.undefined(),
    query: z.undefined(),
    path: z.undefined(),
    response: AutoAssignmentListSchema,
  },
  createAutoAssignmentRule: {
    request: CreateAutoAssignmentRulePayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: AutoAssignmentRuleResponseSchema,
  },
  updateAutoAssignmentRule: {
    request: UpdateAutoAssignmentRulePayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: AutoAssignmentRuleResponseSchema,
  },
  setAutoAssignmentRuleStatus: {
    request: SetAutoAssignmentRuleStatusPayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: AutoAssignmentRuleResponseSchema,
  },
  setAutoAssignmentRuleTitle: {
    request: SetAutoAssignmentRuleTitlePayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: AutoAssignmentRuleResponseSchema,
  },
  deleteAutoAssignmentRule: {
    request: DeleteAutoAssignmentRulePayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: z.undefined(),
  },
  duplicateAutoAssignmentRule: {
    request: DuplicateAutoAssignmentRulePayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: AutoAssignmentRuleResponseSchema,
  },
  sortAutoAssignmentRules: {
    request: SortAutoAssignmentRulesPayloadSchema,
    query: z.undefined(),
    path: z.undefined(),
    response: z.undefined(),
  },
}

export type AutoAssignmentRule = z.infer<typeof AutoAssignmentRuleSchema>
export type AutoAssignmentList = z.infer<typeof AutoAssignmentListSchema>
export type CreateAutoAssignmentRulePayload = z.infer<typeof CreateAutoAssignmentRulePayloadSchema>
export type AutoAssignmentRuleResponse = z.infer<typeof AutoAssignmentRuleResponseSchema>
export type UpdateAutoAssignmentRulePayload = z.infer<typeof UpdateAutoAssignmentRulePayloadSchema>
export type SetAutoAssignmentRuleStatusPayload = z.infer<
  typeof SetAutoAssignmentRuleStatusPayloadSchema
>
export type SetAutoAssignmentRuleTitlePayload = z.infer<
  typeof SetAutoAssignmentRuleTitlePayloadSchema
>
export type DeleteAutoAssignmentRulePayload = z.infer<typeof DeleteAutoAssignmentRulePayloadSchema>
export type DuplicateAutoAssignmentRulePayload = z.infer<
  typeof DuplicateAutoAssignmentRulePayloadSchema
>
export type AutoAssignmentFilterGroup = z.infer<typeof AutoAssignmentFilterGroupSchema>
export type AutoAssignmentFilter = z.infer<typeof AutoAssignmentFilterSchema>
export type AutoAssignmentAction = z.infer<typeof AutoAssignmentActionSchema>
export type SortAutoAssignmentRulesPayload = z.infer<typeof SortAutoAssignmentRulesPayloadSchema>
export type AutoAssignmentCondition = z.infer<typeof AutoAssignmentConditionSchema>
